import { Component } from 'preact';
import { motion, AnimatePresence } from 'framer-motion';
import { ACCENT_GOLD, TEXT_PRIMARY } from '../../lib/medievalTheme';
import { IconCheck } from '../ui/icons';
import { claimOriginFromEvent } from './claimOrigin';
import ClaimableAura from './ClaimableAura';
import { PROGRESSION_DECKS, deckAvatarImageUrl } from '../../utils/arena/starterDecks';
import { DECK_GRANT_LEVELS } from '../../utils/arena/profileDefaults';
import { t } from '../../localization/t';

// Borderless deck reward cell. Fanned-card pattern from the deck
// picker (`transform-origin: bottom center` + per-card rotation),
// scaled down for inline use. No frame, no background. Hover on a
// claimable cell adds a gold halo behind the fan. Click opens the
// existing ArenaStarterPicker via the parent's onClaim callback.
//
// Per-state visual contract:
//   claimable → fan of the next 3 UNOWNED precon avatars (the pool
//               the player can pick from). Subtitle "Choose a precon".
//   received  → SINGLE card showing the SPECIFIC precon the player
//               claimed at this level (looked up by the level's
//               index into starter_decks_owned). Subtitle = deck name.
//   locked    → dim placeholder card backs. Subtitle "Reach this level".

// Solid colors only (no gradient-clip. Same Electron rendering
// reasons as RewardCell).
const PALETTE = {
  claimable: { title: '#fbe9b8', label: TEXT_PRIMARY, state: ACCENT_GOLD },
  claiming:  { title: '#fbe9b8', label: TEXT_PRIMARY, state: ACCENT_GOLD },
  received:  { title: '#c6b486', label: '#7d6e4c',    state: '#7d6e4c' },
  locked:    { title: '#6b6052', label: '#5b5247',    state: '#5b5247' },
};

function pickFanCards(sorceryCards, ownedDeckIds, max = 3) {
  if (!sorceryCards?.length) return [];
  const ownedSet = new Set(ownedDeckIds || []);
  const fan = [];
  for (const deck of PROGRESSION_DECKS) {
    if (ownedSet.has(deck.id)) continue;
    const img = deckAvatarImageUrl(deck, sorceryCards);
    if (!img) continue;
    fan.push({ id: deck.id, name: deck.name, img });
    if (fan.length >= max) break;
  }
  return fan;
}

// Which precon did the player claim at this specific level? The
// starter_decks_owned array is ordered: index 0 = L1 starter, then
// each subsequent index corresponds to the next DECK_GRANT_LEVEL the
// player claimed. So a player who has 4 owned decks claimed at L1,
// L2, L3, L5. In that order. Returns null if the level isn't a
// deck-grant level or the player hasn't reached this index yet.
function deckClaimedAtLevel(level, ownedDeckIds, sorceryCards) {
  const idx = DECK_GRANT_LEVELS.indexOf(level);
  if (idx < 0) return null;
  const deckId = ownedDeckIds?.[idx];
  if (!deckId) return null;
  const deck = PROGRESSION_DECKS.find((d) => d.id === deckId);
  if (!deck) return null;
  return {
    id: deck.id,
    name: deck.name,
    img: deckAvatarImageUrl(deck, sorceryCards),
  };
}

export default class DeckRewardCell extends Component {
  state = { hovered: false };

  handleClick = (e) => {
    e.stopPropagation();
    if (this.props.state !== 'claimable' || !this.props.onClaim) return;
    this.props.onClaim(claimOriginFromEvent(e));
  };

  render() {
    const { level, state, lockedLevel, sorceryCards, ownedDeckIds } = this.props;
    const palette = PALETTE[state] || PALETTE.locked;
    const isClaimable = state === 'claimable';
    const isClaiming = state === 'claiming';
    const isReceived = state === 'received';
    const isLocked = state === 'locked';
    const { hovered } = this.state;

    // Resolve the specific deck claimed at this level for received
    // cells. Claimable cells show the unowned-pool fan; locked cells
    // show dim placeholders so we don't tease decks the player can't
    // currently pick.
    const claimedDeck = isReceived ? deckClaimedAtLevel(level, ownedDeckIds, sorceryCards) : null;
    const poolFan = isClaimable || isClaiming ? pickFanCards(sorceryCards, ownedDeckIds) : [];

    return (
      <motion.button
        type="button"
        disabled={!isClaimable}
        onClick={this.handleClick}
        onMouseEnter={() => this.setState({ hovered: true })}
        onMouseLeave={() => this.setState({ hovered: false })}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          background: 'transparent',
          border: 'none',
          padding: '0.4cqw 0.7cqw',
          // Fixed cell width. Prevents row reflow when the title
          // swaps from "Deck Pick" to a deck name ("Necromancer",
          // "Flamecaller", etc.) on claim. Wide enough to hold the
          // longest precon name in display font.
          width: '14cqw',
          height: '5.6cqw',
          display: 'flex',
          alignItems: 'center',
          gap: '0.9cqw',
          position: 'relative',
          transform: hovered && isClaimable ? 'translateY(-0.2cqw)' : 'translateY(0)',
          transition: 'transform 180ms cubic-bezier(0.2,0.9,0.25,1.05)',
        }}
        aria-label={isClaimable ? t('Open deck picker') : t('Deck pick {state}', { state })}
      >
        {/* Resting "claim me" aura. Same component the regular
            RewardCell uses so all claimable cells (gold/skill/deck)
            share one visual signature. */}
        {isClaimable ? <ClaimableAura /> : null}

        <AnimatePresence>
          {hovered && isClaimable ? (
            <motion.div
              key="halo"
              aria-hidden
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18 }}
              style={{
                position: 'absolute',
                inset: '-0.4cqw',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'inset 0 0 22px rgba(212, 168, 67, 0.32), 0 0 18px rgba(212, 168, 67, 0.35)',
                pointerEvents: 'none',
              }}
            />
          ) : null}
        </AnimatePresence>

        {/* Card art block. Three render paths:
            - received: single card showing the claimed precon
            - claimable / claiming: fan of unowned precons
            - locked: dim placeholder fan */}
        <div
          style={{
            position: 'relative',
            width: '4.6cqw',
            height: '5.2cqw',
            flexShrink: 0,
          }}
        >
          {isReceived && claimedDeck ? (
            <div
              className="absolute card-mask"
              style={{
                left: '0.8cqw',
                top: 0,
                width: '3cqw',
                height: '4.2cqw',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.7)) saturate(0.65) brightness(0.78)',
              }}
            >
              {claimedDeck.img ? (
                <img src={claimedDeck.img} alt={claimedDeck.name} className="w-full h-full object-cover" draggable={false} />
              ) : null}
            </div>
          ) : isLocked ? (
            Array.from({ length: 3 }).map((_, idx, arr) => {
              const midIdx = (arr.length - 1) / 2;
              const angle = (idx - midIdx) * 9;
              const xOffset = idx * 0.55;
              return (
                <div
                  key={idx}
                  className="absolute card-mask"
                  style={{
                    left: `${xOffset}cqw`,
                    top: 0,
                    width: '3cqw',
                    height: '4.2cqw',
                    transform: `rotate(${angle}deg)`,
                    transformOrigin: 'bottom center',
                    zIndex: 10 + idx,
                    background: 'linear-gradient(180deg, #1c1814 0%, #0e0a08 100%)',
                    border: '1px solid #2a2218',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.7))',
                  }}
                />
              );
            })
          ) : (
            poolFan.map((d, idx, arr) => {
              const midIdx = (arr.length - 1) / 2;
              const angle = (idx - midIdx) * 9;
              const xOffset = idx * 0.55;
              return (
                <div
                  key={d.id}
                  className="absolute card-mask"
                  style={{
                    left: `${xOffset}cqw`,
                    top: 0,
                    width: '3cqw',
                    height: '4.2cqw',
                    transform: `rotate(${angle}deg)`,
 transformOrigin: 'bottom center',
 zIndex: 10 + idx,
 filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.7))',
 }}
 >
 <img src={d.img} alt={d.name} className="w-full h-full object-cover" draggable={false} />
 </div>
 );
 })
 )}
 </div>

 <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.1cqw', minWidth: 0 }}>
 <span
 className="arena-heading font-bold"
 style={{
 fontSize: '1.2cqw',
 lineHeight: 1,
 color: palette.title,
 textShadow: '0 1px 2px rgba(0,0,0,0.75)',
 }}
 >
 {isReceived && claimedDeck ? claimedDeck.name : t('Deck Pick')}
 </span>
 <span
 style={{
 fontSize: '0.55cqw',
 color: palette.label,
 fontWeight: 700,
 textShadow: '0 1px 2px rgba(0,0,0,0.7)',
 whiteSpace: 'nowrap',
 }}
 >
 {isLocked ? t('Reach this level') : isReceived ? t('Precon') : t('Choose a precon')}
 </span>
 <span
 style={{
 fontSize: '0.5cqw',
 color: palette.state,
 fontWeight: 700,
 marginTop: '0.1cqw',
 textShadow: '0 1px 2px rgba(0,0,0,0.7)',
 whiteSpace: 'nowrap',
 display: 'flex',
 alignItems: 'center',
 gap: '0.3cqw',
 // Reserved width for the same reason as RewardCell.
 // "Opening..." and "Received" are wider than "Claim".
 width: '4.4cqw',
 height: '0.65cqw',
 }}
 >
 {isReceived ? (
 <>
 <IconCheck size="0.65cqw" color={palette.state} />
 {t('Received')}
 </>
 ) : isClaiming ? (
 t('Opening...')
 ) : isClaimable ? (
 t('Claim')
 ) : (
 t('Lvl {n}', { n: lockedLevel })
            )}
          </span>
        </div>
      </motion.button>
    );
  }
}
